import { Badge } from "@packages/ui/src/components/shadcn/badge";
import { Card, CardContent, CardFooter } from "@packages/ui/src/components/shadcn/card";
import { Button } from "@packages/ui/src/components/shadcn/button";
import { NotionEvent } from "@/lib/notion/types/eventsTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faMapMarkerAlt,
  faUsers,
  faUser,
  faExternalLinkAlt,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import Image from "next/image";

type BadgeVariant = "default" | "destructive" | "secondary" | "outline" | null;
const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  "Not started": "destructive",
  Planning: "default",
  "Pending review": "outline",
  "Registration open": "secondary",
  Done: "default",
  Cancelled: "destructive",
};
const FORMAT_VARIANTS: Record<string, BadgeVariant> = {
  Virtual: "outline",
  "In person": "secondary",
  Hybrid: "default",
};
function getStatusVariant(status: string): BadgeVariant {
  return STATUS_VARIANTS[status] ?? "default";
}
function getFormatVariant(format: string): BadgeVariant {
  return FORMAT_VARIANTS[format] ?? "default";
}
interface EventCardProps {
  event: NotionEvent;
}
export function EventCard({ event }: EventCardProps) {
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  return (
    <Card className="group relative overflow-hidden border-0 rounded-xl shadow-lg hover:shadow-2xl hover:shadow-slate-900/10 transition-all duration-300 hover:-translate-y-1">
      <div className="relative h-48 rounded-t-xl overflow-hidden">
        <Image
          src={event.foto && event.foto.length > 0 ? event.foto[0] : "/esempio.jpg"}
          alt={event.eventName}
          fill
          unoptimized
          className="absolute inset-0 bg-slate-900 object-cover group-hover:scale-105 transition-transform duration-300"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute top-4 right-4 z-10">
          <Badge
            variant={getStatusVariant(event.status)}
            className="text-xs font-semibold"
          >
            {event.status}
          </Badge>
        </div>
        <div className="absolute bottom-4 left-4 z-10">
          <Badge className="bg-white/10 text-white font-medium px-3 py-1 rounded-full border-0 tracking-wider">
            {event.category}
          </Badge>
        </div>
      </div>

      <CardContent className="p-6 pb-5">
        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-tight">
          {event.eventName}
        </h3>

        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendar}
              className="w-4 h-4 text-gray-400"
            />
            <span>{formatDate(event.eventDate)}</span>
          </div>
          {event.venue && (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="w-4 h-4 text-gray-400"
              />
              <span className="truncate">{event.venue}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-gray-400" />
            <Badge
              variant={getFormatVariant(event.format)}
              className="text-xs font-semibold"
            >
              {event.format}
            </Badge>
          </div>
          {event.capacity !== null && (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faUsers}
                className="w-4 h-4 text-gray-400"
              />
              <span>{event.capacity} posti</span>
            </div>
          )}
          {event.eventOwner && (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faUser}
                className="w-4 h-4 text-gray-400"
              />
              <span className="truncate">{event.eventOwner}</span>
            </div>
          )}
        </div>
      </CardContent>

      {event.eventUrl ? (
        <CardFooter className="p-6 pt-0 pb-6">
          <Button
            asChild
            className="w-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <a
              href={event.eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2"
            >
              Vai all&apos;evento
              <FontAwesomeIcon icon={faExternalLinkAlt} className="w-4 h-4" />
            </a>
          </Button>
        </CardFooter>
      ) : (
        <div className="pb-6" />
      )}
    </Card>
  );
}
